import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard,
  KanbanSquare, 
  Users, 
  ListTodo,
  BarChart3,
  Settings,
  Bell,
  Menu,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
  X,
  ShieldAlert,
  LogOut,
  Calendar,
  Store,
  Loader2,
  Layers,
  Zap,
  ClipboardList,
  Receipt,
  Brain,
  RotateCcw,
  BarChart,
  BookOpen
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { notificationAllowed, claimNotification, useNotificationPreferencesStore } from '@/store/notificationPreferencesStore';
import { useAuthStore } from '@/store/authStore';
import { useRolePermissions } from '@/hooks/useRolePermissions';
import { useLeadsStore } from '@/store/leadsStore';
import { useThemeStore } from '@/store/themeStore';
import { useAppointmentsStore } from '@/store/appointmentsStore';
import { useTenant } from '@/hooks/useTenant';
import { useAdminStore } from '@/store/adminStore';
import { useLojaAtivaStore } from '@/store/lojaAtivaStore';
import { useConversationsStore } from '@/store/conversationsStore';
import { useLojasStore } from '@/store/lojasStore';
import { useConfigStore } from '@/store/configStore';
import { useUsersStore } from '@/store/usersStore';
import { useWhatsAppStore } from '@/store/whatsappStore';
import { useCatalogStore } from '@/store/catalogStore';
import { useAiModuleStore } from '@/store/aiModuleStore';
import { ChangelogButton } from '@/components/ChangelogButton';
import { UpgradeModal } from '@/components/plan/UpgradeModal';
import { SupportChatButton } from '@/components/support/SupportChatButton';
// import { NotificationBellDropdown } from '@/components/notifications/NotificationBellDropdown'; // — REMOVIDO v29.1
import { supabase } from '@/lib/supabase';
import { fetchWithAuth } from '@/services/api';
import { useAutoReloadOnIdle } from '@/hooks/useAutoReloadOnIdle';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Caixa de Entrada', href: '/inbox', icon: MessageSquare },
  { name: 'Kanban', href: '/kanban', icon: KanbanSquare },
  { name: 'Lista de leads', href: '/leads', icon: Users },
  { name: 'Agenda', href: '/agenda', icon: Calendar },
  { name: 'Catálogo', href: '/catalog', icon: Layers, catalogOnly: true },
  { name: 'Automações Inteligentes', href: '/automacoes-ia', icon: Brain, aiModuleOnly: true },
  { name: 'Relatórios', href: '/relatorios', icon: BarChart3 },
  { name: 'Configurações', href: '/config', icon: Settings, roles: ['super_admin', 'tenant_admin', 'gestor', 'owner_dev', 'admin', 'vendedor'] },
  { name: 'Painel Admin', href: '/admin', icon: ShieldAlert, roles: ['super_admin'] },
  { name: 'Gerador de Notas', href: '/notas-campo', icon: Receipt, isBeta: true },
];

export function Layout() {
  useAutoReloadOnIdle();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { isDarkMode, toggleDarkMode } = useThemeStore();

  const [notifications, setNotifications] = useState<any[]>([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [loadingNotifs, setLoadingNotifs] = useState(false);

  const profile = useAuthStore(state => state.profile);
  const notificationIdentity = useAuthStore(state => state.session?.user?.id);
  useEffect(() => {
    setNotifications([]);
    setLoadingNotifs(false);
    setShowNotifDropdown(false);
  }, [notificationIdentity]);
  const { canViewReports } = useRolePermissions();
  const { lojaAtivaId, lojasAcessiveis, setLojaAtiva } = useLojaAtivaStore();
  const logout = useAuthStore(state => state.logout);
  const { tenant, isSuperAdmin, tenantName, tenantLogo, tenantColor } = useTenant();

  const fetchLeads = useLeadsStore(state => state.fetchLeads);
  const todayCount = useAppointmentsStore(state => state.todayCount);
  const fetchTodayCount = useAppointmentsStore(state => state.fetchTodayCount);
  const tenants = useAdminStore(state => state.tenants);
  const fetchTenants = useAdminStore(state => state.fetchTenants);
  const [isMobile, setIsMobile] = useState(false);

  const catalogEnabled = useCatalogStore(state => state.catalogEnabled);
  const fetchCatalogConfig = useCatalogStore(state => state.fetchConfig);
  const aiModuleEnabled = useAiModuleStore(state => state.aiModuleEnabled);
  const fetchAiModuleStatus = useAiModuleStore(state => state.fetchAiModuleStatus);

  const pendingAdminCount = tenants.filter(t => t.status === 'pendente').length;
  const [hasDisconnectedWhatsApp, setHasDisconnectedWhatsApp] = useState(false);

  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState<string | undefined>(undefined);

  const mainContentRef = useRef<HTMLDivElement>(null);

  // Scroll to top automatically when changing pages/routes
  useEffect(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, [location.pathname]);

  useEffect(() => {
    const handlePlanLimit = (e: any) => {
      const msg = e.detail?.message || 'Limite do plano excedido.';
      toast.error(msg, { id: 'plan-limit-toast', duration: 5000 });
      setUpgradeMessage(msg);
      setIsUpgradeModalOpen(true);
    };

    window.addEventListener('plan-limit-exceeded', handlePlanLimit as EventListener);
    return () => window.removeEventListener('plan-limit-exceeded', handlePlanLimit as EventListener);
  }, []);

  const getPlanBadgeConfig = (planoRaw?: string) => {
    const p = (planoRaw || 'grátis').toString().toLowerCase().trim();
    switch (p) {
      case 'starter':
        return { label: 'Starter', className: 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40', iconColor: 'text-blue-600 dark:text-blue-400' };
      case 'pro':
        return { label: 'Pro', className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 hover:bg-emerald-100 dark:hover:bg-emerald-900/40', iconColor: 'text-emerald-600 dark:text-emerald-400' };
      case 'personalizado':
        return { label: 'Personalizado', className: 'bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border-purple-200 dark:border-purple-800 hover:bg-purple-100 dark:hover:bg-purple-900/40', iconColor: 'text-purple-600 dark:text-purple-400' };
      default:
        return { label: 'Grátis', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700', iconColor: 'text-gray-500 dark:text-gray-400' };
    }
  };

  useEffect(() => {
    fetchCatalogConfig();
  }, [fetchCatalogConfig]);

  useEffect(() => {
    let effectiveTenantId: string | null | undefined = profile?.tenant_id;

    // Super admin: usa o tenant da LOJA selecionada, não da própria conta
    if (profile?.role === 'super_admin' && lojaAtivaId) {
      const lojaAtiva = lojasAcessiveis?.find((l: any) => l.id === lojaAtivaId);
      if (lojaAtiva?.tenant_id) {
        effectiveTenantId = lojaAtiva.tenant_id;
      }
    }

    if (effectiveTenantId) {
      fetchAiModuleStatus(effectiveTenantId);
    }
  }, [profile, lojaAtivaId, lojasAcessiveis, fetchAiModuleStatus]);

  const checkWhatsAppStatus = useCallback(async () => {
    if (!profile?.tenant_id) return;
    try {
      const { data, error } = await supabase
        .from('crm_whatsapp_connections')
        .select('status, updated_at')
        .eq('tenant_id', profile.tenant_id);

      if (error) {
        console.error('[LAYOUT] Erro ao buscar status do WhatsApp:', error);
        return;
      }

      if (!data || data.length === 0) {
        setHasDisconnectedWhatsApp(false);
        return;
      }

      // Check if any instance is disconnected for more than 1 hour
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      const hasBadConnection = data.some(conn => {
        if (conn.status === 'disconnected') {
          const updatedAt = conn.updated_at ? new Date(conn.updated_at) : new Date();
          return updatedAt < oneHourAgo;
        }
        return false;
      });

      setHasDisconnectedWhatsApp(hasBadConnection);
    } catch (err) {
      console.error('[LAYOUT] Erro ao checar status do WhatsApp:', err);
    }
  }, [profile?.tenant_id]);

  useEffect(() => {
    checkWhatsAppStatus();
  }, [checkWhatsAppStatus]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchTenants(true);
    }
  }, [isSuperAdmin, fetchTenants]);

  // Dynamically set tenant primary color in CSS variables
  useEffect(() => {
    document.documentElement.style.setProperty('--tenant-primary', tenantColor);
  }, [tenantColor]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    
    const handleOpenSidebar = () => setSidebarOpen(true);
    window.addEventListener('open-mobile-sidebar', handleOpenSidebar);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('open-mobile-sidebar', handleOpenSidebar);
    };
  }, []);

  const fetchNotifications = async () => {
    const identity = useAuthStore.getState().session?.user?.id;
    if (!identity) return;
    setLoadingNotifs(true);
    try {
      await useNotificationPreferencesStore.getState().load(identity);
      if (useAuthStore.getState().session?.user?.id !== identity) return;
      const response = await fetchWithAuth('/api/my-notifications');
      if (response.ok) {
        const data = await response.json();
        if (useAuthStore.getState().session?.user?.id !== identity) return;
        // Side effects stay outside React state updaters (StrictMode may replay them).
        for (const notif of (Array.isArray(data) ? data : [])) {
          if (notif.status !== 'notified' || !claimNotification(identity, 'reminders', String(notif.id || ''))) continue;
          if (notificationAllowed(identity, 'reminders', 'toast')) toast(`Lembrete: ${notif.titulo || 'Novo lembrete'}`, { id: `reminder:${notif.id}`, icon: '⏰', duration: 8000 });
          if (notificationAllowed(identity, 'reminders', 'sound')) {
            let audioContext: AudioContext | undefined;
            try {
              const AC = window.AudioContext || (window as any).webkitAudioContext;
              if (!AC) continue;
              audioContext = new AC();
              const oscillator = audioContext.createOscillator();
              const gain = audioContext.createGain();
              oscillator.connect(gain);
              gain.connect(audioContext.destination);
              oscillator.frequency.value = 800;
              gain.gain.setValueAtTime(0.3, audioContext.currentTime);
              gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
              oscillator.onended = () => { void audioContext?.close().catch(() => {}); };
              oscillator.start();
              oscillator.stop(audioContext.currentTime + 0.5);
            } catch {
              void audioContext?.close().catch(() => {});
            }
          }
        }
        setNotifications(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('[LAYOUT] Erro ao buscar notificações:', err);
    } finally {
      if (useAuthStore.getState().session?.user?.id === identity) setLoadingNotifs(false);
    }
  };

  const handleMarkAsDone = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const response = await fetchWithAuth(`/api/reminders/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'done' })
      });
      if (response.ok) {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, status: 'done' } : n));
        toast.success('Lembrete concluído');
      }
    } catch (err) {
      console.error('[LAYOUT] Erro ao marcar lembrete como concluído:', err);
    }
  };

  // Realtime listener for user reminders
  useEffect(() => {
    if (!profile?.id) return;

    fetchNotifications();

    const channel = supabase
      .channel(`user-reminders:${profile.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'crm_lead_reminders',
          filter: `user_id=eq.${profile.id}`
        },
        () => {
          fetchNotifications();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id]);

  // Initial priority data load
  useEffect(() => {
    if (!profile?.id) return;

    const loadEssentialData = async () => {
      try {
        await Promise.allSettled([
          useLeadsStore.getState().fetchLeads({ loja_id: lojaAtivaId || undefined }),
          useConversationsStore.getState().fetchConversations()
        ]);
      } catch (err) {
        console.error('[LAYOUT] Error loading priority initial data:', err);
      }

      try {
        await Promise.allSettled([
          useLojasStore.getState().fetchLojas(),
          useConfigStore.getState().fetchConfigs(),
          useUsersStore.getState().fetchUsers(true),
          useAppointmentsStore.getState().fetchTodayCount(),
          useWhatsAppStore.getState().fetchConnections()
        ]);
      } catch (err) {
        console.error('[LAYOUT] Error loading secondary data:', err);
      }
    };

    loadEssentialData();
  }, [profile?.id, lojaAtivaId]);

  // Master background sync cycle (2 min) + focus/visibility handler
  useEffect(() => {
    if (!profile?.id) return;

    let syncCounter = 0;
    let lastFocusSync = Date.now();

    const masterSync = async () => {
      if (document.hidden) return;

      try {
        // 1. Session check
        const profileRes = await fetchWithAuth('/api/profile');
        if (profileRes.status === 401) {
          toast.error('Sessão expirada. Por favor, faça login novamente.');
          useAuthStore.getState().logout();
          navigate('/login');
          return;
        }

        // 2. Data & notification sync
        const activeLojaId = useLojaAtivaStore.getState().lojaAtivaId;
        await Promise.allSettled([
          fetchNotifications(),
          useLeadsStore.getState().fetchLeads({ loja_id: activeLojaId || undefined, silent: true }),
          useConversationsStore.getState().fetchConversations({ loja_id: activeLojaId || undefined, silent: true }),
          useAppointmentsStore.getState().fetchTodayCount()
        ]);

        // 3. WhatsApp connection status check every 2 cycles (~4 minutes)
        syncCounter++;
        if (syncCounter >= 2) {
          syncCounter = 0;
          await checkWhatsAppStatus();
        }
      } catch (e) {
        // Silent catch for network errors
      }
    };

    const masterInterval = setInterval(masterSync, 120000);

    const handleFocus = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        if (now - lastFocusSync >= 60000) {
          lastFocusSync = now;
          masterSync();
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleFocus);

    return () => {
      clearInterval(masterInterval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [profile?.id, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getRoleBadge = (role?: string) => {
    switch (role) {
      case 'super_admin':
        return { label: 'Super Admin', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' };
      case 'tenant_admin':
        return { label: 'Dono', className: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200' };
      case 'gestor':
        return { label: 'Gerente', className: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200' };
      case 'vendedor':
        return { label: 'Vendedor', className: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200' };
      default:
        return { label: role || 'Usuário', className: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' };
    }
  };

  const roleBadge = getRoleBadge(profile?.role);
  const hasUnreadNotifs = notifications.some(n => n.status === 'notified');

  return (
    <div className="flex h-screen bg-[#F7FAFC] dark:bg-[#0B0E11] font-sans transition-colors duration-300 relative">
      {/* Sidebar Desktop - z-50 para garantir que a barra lateral e o botão de toggle fiquem na camada da frente do topo/header (z-20/z-30) */}
      <aside id="desktop-navigation" className={cn(
        "hidden md:flex flex-col bg-white dark:bg-[#1A1D21] border-r border-gray-200 dark:border-gray-800 transition-all duration-300 relative z-50",
        isCollapsed ? "w-[72px]" : "w-64"
      )}>
        {/* Botão de colapsar/expandir com z-[60] para sobrepor qualquer header ou container adjacente sem ser cortado */}
        <button 
          aria-label={isCollapsed ? 'Expandir navegação' : 'Recolher navegação'}
          aria-expanded={!isCollapsed}
          aria-controls="desktop-navigation"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3.5 top-5 bg-white dark:bg-[#1A1D21] border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-full p-1 z-[60] shadow-md transition-transform hover:scale-110 cursor-pointer focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-blue-600"
        >
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>

        <div className={cn(
          "h-16 flex items-center border-b border-gray-100 dark:border-gray-800 overflow-hidden",
          isCollapsed ? "justify-center px-0" : "px-6"
        )}>
          {tenantLogo ? (
            <img src={tenantLogo} alt={tenantName} className="w-6 h-6 object-contain flex-shrink-0" />
          ) : (
            <MessageSquare className={cn("w-6 h-6 text-[var(--tenant-primary)] flex-shrink-0", !isCollapsed && "mr-2")} />
          )}
          {!isCollapsed && (
            <span className="font-bold text-lg text-gray-900 dark:text-white whitespace-nowrap truncate ml-2">
              {tenantName}
            </span>
          )}
        </div>
        
        <nav className="flex-1 overflow-y-auto py-4 overflow-x-hidden">
          <ul className="space-y-1.5 px-3">
            {navigation.map((item) => {
              if (item.name === 'Relatórios' && !canViewReports) return null;
              if ((item as any).catalogOnly && !catalogEnabled) return null;
              if ((item as any).aiModuleOnly && !aiModuleEnabled) return null;
              if (item.roles && (!profile || (!item.roles.includes(profile.role) && !(isSuperAdmin && item.roles.includes('super_admin'))))) return null;
              if ((item as any).isBeta) {
                const canSeeBeta = isSuperAdmin;
                if (!canSeeBeta) return null;
              }
              
              const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));
              return (
                <li key={item.name}>
                  <Link
                    to={item.href}
                    title={isCollapsed ? item.name : undefined}
                    className={cn(
                      "flex items-center text-sm font-medium rounded-xl transition-colors relative",
                      isCollapsed ? "justify-center h-10 w-10 mx-auto p-0" : "px-3 py-2.5 w-full",
                      isActive 
                        ? "bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] font-bold" 
                        : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/70 hover:text-gray-900 dark:hover:text-white"
                    )}
                  >
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <item.icon className={cn("w-5 h-5", isActive ? "text-[var(--tenant-primary)]" : "text-gray-400")} />
                    </div>
                    {!isCollapsed && <span className="truncate flex-1 ml-3">{item.name}</span>}
                    {item.name === 'Agenda' && todayCount > 0 && (
                      <span className={cn(
                        "bg-[#2563EB] text-white text-[10px] font-bold rounded-full flex items-center justify-center shrink-0 shadow-2xs",
                        isCollapsed ? "absolute -top-0.5 -right-0.5 w-4 h-4 text-[9px]" : "px-2 py-0.5 ml-2"
                      )}>
                        {todayCount}
                      </span>
                    )}
                    {item.name === 'Painel Admin' && pendingAdminCount > 0 && (
                      <span className={cn(
                        "bg-rose-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center shrink-0 shadow-2xs animate-pulse",
                        isCollapsed ? "absolute -top-0.5 -right-0.5 w-4 h-4 text-[9px]" : "px-2 py-0.5 ml-2"
                      )}>
                        {pendingAdminCount}
                      </span>
                    )}
                    {item.name === 'Configurações' && hasDisconnectedWhatsApp && (
                      <span className={cn(
                        "bg-rose-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center shrink-0 shadow-2xs animate-pulse",
                        isCollapsed ? "absolute -top-0.5 -right-0.5 w-4 h-4 text-[9px]" : "px-2 py-0.5 ml-2"
                      )}>
                        !
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        
        <div className="p-3 border-t border-gray-200 dark:border-gray-800 overflow-hidden flex flex-col gap-2">
          {/* [TUTORIAIS] Grupo de ajuda: Tutoriais → Assistente IA → novidades */}
          <Link
            to="/tutoriais"
            title={isCollapsed ? "Tutoriais" : "Central de Tutoriais"}
            aria-label="Tutoriais"
            className={cn(
              "flex items-center text-sm font-medium rounded-xl transition-colors relative focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--tenant-primary)]",
              isCollapsed ? "justify-center h-10 w-10 mx-auto p-0" : "px-3 py-2 w-full",
              (location.pathname === '/tutoriais' || location.pathname.startsWith('/tutoriais/'))
                ? "bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] font-bold"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/70 hover:text-gray-900 dark:hover:text-white"
            )}
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <BookOpen className={cn("w-5 h-5", (location.pathname === '/tutoriais' || location.pathname.startsWith('/tutoriais/')) ? "text-[var(--tenant-primary)]" : "text-gray-400")} />
            </div>
            {!isCollapsed && <span className="truncate flex-1 ml-3">Tutoriais</span>}
          </Link>
          <SupportChatButton isCollapsed={isCollapsed} />
          <ChangelogButton isCollapsed={isCollapsed} />
          <div className={cn("flex items-center", isCollapsed && "justify-center")}>
            <div className="w-8 h-8 rounded-full bg-[var(--tenant-primary)] flex-shrink-0 flex items-center justify-center text-white font-bold" title={profile?.nome || 'Usuário'}>
              {profile?.nome?.charAt(0).toUpperCase() || 'U'}
            </div>
            {!isCollapsed && (
              <div className="ml-3 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{profile?.nome || 'Usuário'}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize truncate">{profile?.role || ''}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-30 md:hidden transition-opacity duration-300"
            onClick={() => setSidebarOpen(false)}
          />
          <aside id="mobile-navigation" className="fixed inset-y-0 left-0 w-64 bg-white dark:bg-[#1A1D21] z-40 md:hidden flex flex-col border-r border-gray-200 dark:border-gray-800 shadow-xl animate-in slide-in-from-left duration-200">
            <div className="h-16 flex items-center justify-between px-6 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center min-w-0">
                {tenantLogo ? (
                  <img src={tenantLogo} alt={tenantName} className="w-6 h-6 object-contain flex-shrink-0 mr-2" />
                ) : (
                  <MessageSquare className="w-6 h-6 text-[var(--tenant-primary)] mr-2 flex-shrink-0" />
                )}
                <span className="font-bold text-lg text-gray-900 dark:text-white truncate">
                  {tenantName}
                </span>
              </div>
              <button 
                aria-label="Fechar navegação"
                onClick={() => setSidebarOpen(false)}
                className="text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white p-1 rounded-md ml-2 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              >
                <X size={20} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto py-4">
              <ul className="space-y-1 px-3">
                {navigation.map((item) => {
                  if ((item as any).catalogOnly && !catalogEnabled) return null;
                  if ((item as any).aiModuleOnly && !aiModuleEnabled) return null;
                  if (item.roles && (!profile || (!item.roles.includes(profile.role) && !(isSuperAdmin && item.roles.includes('super_admin'))))) return null;
                  if ((item as any).isBeta) {
                    const canSeeBeta = isSuperAdmin;
                    if (!canSeeBeta) return null;
                  }
                  const isActive = location.pathname === item.href;
                  return (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={cn(
                          "flex items-center text-sm font-medium rounded-md px-3 py-2.5 transition-colors",
                          isActive 
                            ? "bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)]" 
                            : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white"
                        )}
                      >
                        <item.icon className={cn("w-5 h-5 flex-shrink-0 mr-3", isActive ? "text-[var(--tenant-primary)]" : "text-gray-400")} />
                        <span className="flex-1">{item.name}</span>
                        {item.name === 'Agenda' && todayCount > 0 && (
                          <span className="bg-[#2563EB] text-white text-xs font-bold px-2 py-0.5 rounded-full shrink-0">
                            {todayCount}
                          </span>
                        )}
                        {item.name === 'Painel Admin' && pendingAdminCount > 0 && (
                          <span className="bg-rose-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shrink-0 animate-pulse">
                            {pendingAdminCount}
                          </span>
                        )}
                        {item.name === 'Configurações' && hasDisconnectedWhatsApp && (
                          <span className="bg-rose-600 text-white text-xs font-bold px-2 py-0.5 rounded-full shrink-0 animate-pulse">
                            !
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="p-4 border-t border-gray-200 dark:border-gray-800 space-y-3">
              {/* [TUTORIAIS] Link no rodapé mobile */}
              <Link
                to="/tutoriais"
                onClick={() => setSidebarOpen(false)}
                title="Central de Tutoriais"
                aria-label="Tutoriais"
                className={cn(
                  "flex items-center px-3 py-2 w-full text-sm font-medium rounded-xl transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--tenant-primary)]",
                  (location.pathname === '/tutoriais' || location.pathname.startsWith('/tutoriais/'))
                    ? "bg-[var(--tenant-primary)]/10 text-[var(--tenant-primary)] font-bold"
                    : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/70 hover:text-gray-900 dark:hover:text-white"
                )}
              >
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <BookOpen className={cn("w-5 h-5", (location.pathname === '/tutoriais' || location.pathname.startsWith('/tutoriais/')) ? "text-[var(--tenant-primary)]" : "text-gray-400")} />
                </div>
                <span className="truncate flex-1 ml-3">Tutoriais</span>
              </Link>
              <SupportChatButton isCollapsed={false} />
              <ChangelogButton isCollapsed={false} />
              <div className="flex items-center justify-between">
                <Link 
                  to="/perfil" 
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center min-w-0 hover:opacity-85 transition-opacity"
                >
                  <div className="w-8 h-8 rounded-full bg-[var(--tenant-primary)] flex-shrink-0 flex items-center justify-center text-white font-bold text-xs overflow-hidden">
                    {profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt={profile.nome} className="w-full h-full object-cover" />
                    ) : (
                      profile?.nome?.charAt(0).toUpperCase() || 'U'
                    )}
                  </div>
                  <div className="ml-3 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{profile?.nome || 'Usuário'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 capitalize truncate">{profile?.role || ''}</p>
                  </div>
                </Link>
                <button 
                  onClick={() => toggleDarkMode()}
                  className="p-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-full transition-colors ml-2"
                >
                  {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                </button>
              </div>
              <button
                onClick={() => {
                  setSidebarOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center justify-center gap-2 py-2 bg-red-50 hover:bg-red-100 dark:bg-red-950/20 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 text-sm font-medium rounded-md transition-colors"
              >
                <LogOut size={16} />
                Sair da Conta
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden pt-0 md:pt-0">
        {/* Top Header Mobile */}
        <header className="flex md:hidden items-center justify-between px-4 bg-white dark:bg-[#1A1D21] border-b border-gray-200 dark:border-gray-800 shadow-sm z-30 h-14 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <button 
              aria-label="Abrir navegação"
              aria-expanded={sidebarOpen}
              aria-controls={sidebarOpen ? 'mobile-navigation' : undefined}
              className="p-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-blue-600"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              {tenantLogo ? (
                <img src={tenantLogo} alt="TASK CRM" className="w-5 h-5 object-contain flex-shrink-0" />
              ) : (
                <MessageSquare className="w-5 h-5 text-[var(--tenant-primary)] flex-shrink-0" />
              )}
              <span className="font-bold text-gray-900 dark:text-white text-sm truncate">
                TASK CRM
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {lojasAcessiveis.length === 1 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60 rounded-lg max-w-[140px]">
                <Store className="w-3 h-3 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 truncate">
                  {lojasAcessiveis[0]?.nome}
                </span>
              </div>
            )}
            {lojasAcessiveis.length > 1 && (
              <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60 rounded-lg max-w-[100px] transition-all">
                <Store className="w-3 h-3 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                <select
                  aria-label="Loja ativa"
                  value={lojaAtivaId || ''}
                  onChange={(e) => setLojaAtiva(e.target.value)}
                  className="bg-transparent text-[10px] font-bold text-gray-700 dark:text-gray-300 border-none outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-blue-600 min-h-6 cursor-pointer pr-4 truncate py-0"
                >
                  {lojasAcessiveis.map((l) => (
                    <option key={l.id} value={l.id} className="bg-white dark:bg-[#111B21] text-gray-900 dark:text-white text-[10px]">
                      {l.nome}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <button 
              aria-label={isDarkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
              onClick={() => toggleDarkMode()}
              className="p-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-full transition-colors active:scale-95 focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-blue-600"
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* <NotificationBellDropdown /> — REMOVIDO v29.1 */}

            <Link 
              to="/perfil"
              className="w-7 h-7 rounded-full bg-[var(--tenant-primary)] flex-shrink-0 flex items-center justify-center text-white font-bold text-xs overflow-hidden"
              title="Meu Perfil"
            >
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt={profile.nome} className="w-full h-full object-cover" />
              ) : (
                profile?.nome?.charAt(0).toUpperCase() || 'U'
              )}
            </Link>
          </div>
        </header>

        {/* Top Header Desktop - z-20 para permitir que o botão de toggle da sidebar (z-[60]) fique visível na camada superior */}
        <header className={cn(
          "hidden md:flex items-center justify-between px-4 sm:px-6 lg:px-8 bg-white dark:bg-[#1A1D21] border-b border-gray-200 dark:border-gray-800 shadow-sm z-20 relative transition-all",
          "h-16"
        )}>
            <div className="flex items-center flex-1 min-w-0 gap-3">
              <button 
                aria-label="Abrir navegação"
                aria-expanded={sidebarOpen}
                aria-controls={sidebarOpen ? 'mobile-navigation' : undefined}
                className="p-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white md:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </button>
              
              {/* Header Header Info */}
              <div className="flex items-center gap-2 min-w-0">
                {tenantLogo ? (
                  <img src={tenantLogo} alt="TASK CRM" className="w-6 h-6 object-contain flex-shrink-0" />
                ) : null}
                <span className="font-bold text-gray-900 dark:text-white text-sm md:text-base truncate">
                  TASK CRM
                </span>
                {!tenant && !isSuperAdmin && (
                  <span title="Carregando dados da empresa..." className="flex items-center ml-1.5 shrink-0">
                    <Loader2 className="w-4 h-4 text-[var(--tenant-primary)] animate-spin" />
                  </span>
                )}
              </div>

              <div className="hidden lg:block ml-6 flex-1" />
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-3">
              {lojasAcessiveis.length > 1 && (
                <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700/60 rounded-lg max-w-[150px] sm:max-w-xs transition-all">
                  <Store className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                  <select
                    aria-label="Loja ativa"
                    value={lojaAtivaId || ''}
                    onChange={(e) => setLojaAtiva(e.target.value)}
                    className="bg-transparent text-[11px] font-bold text-gray-700 dark:text-gray-300 border-none outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-blue-600 min-h-6 cursor-pointer pr-4 truncate py-0"
                  >
                    {lojasAcessiveis.map((l) => (
                      <option key={l.id} value={l.id} className="bg-white dark:bg-[#111B21] text-gray-900 dark:text-white">
                        {l.nome}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {lojasAcessiveis.length === 0 && profile?.role !== 'super_admin' && (
                <span className="text-[10px] font-bold text-red-800 bg-red-100 dark:bg-red-950/40 dark:text-red-400 border border-red-200 dark:border-red-900/40 px-2 py-0.5 rounded-full select-none flex items-center gap-1">
                  <Store className="w-3 h-3" />
                  Sem loja
                </span>
              )}

              {/* Profile Avatar + Name + Role Badge */}
              <Link 
                to="/perfil"
                className="flex items-center gap-2 pr-2 border-r border-gray-200 dark:border-gray-800 hover:opacity-85 transition-opacity cursor-pointer group"
                title="Meu Perfil"
              >
                <div className="w-8 h-8 rounded-full bg-[var(--tenant-primary)] flex-shrink-0 flex items-center justify-center text-white font-bold text-xs overflow-hidden">
                  {profile?.avatar_url ? (
                    <img src={profile.avatar_url} alt={profile.nome} className="w-full h-full object-cover" />
                  ) : (
                    profile?.nome?.charAt(0).toUpperCase() || 'U'
                  )}
                </div>
                <div className="hidden sm:flex flex-col text-left">
                  <span className="text-xs font-semibold text-gray-900 dark:text-white leading-tight truncate max-w-[120px] group-hover:text-[var(--tenant-primary)] transition-colors">
                    {profile?.nome || 'Usuário'}
                  </span>
                  <span className={cn("text-[10px] font-bold px-1.5 py-0.5 rounded w-fit mt-0.5", roleBadge.className)}>
                    {roleBadge.label}
                  </span>
                </div>
              </Link>

              {/* Plan Badge */}
              {tenant && (
                <button 
                  onClick={() => {
                    setUpgradeMessage(undefined);
                    setIsUpgradeModalOpen(true);
                  }}
                  title="Plano do Tenant — Clique para fazer upgrade"
                  className={cn(
                    "hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold border transition-all cursor-pointer shadow-2xs active:scale-95",
                    getPlanBadgeConfig(tenant.plano).className
                  )}
                >
                  <Zap size={13} className={getPlanBadgeConfig(tenant.plano).iconColor} />
                  <span className="capitalize">{getPlanBadgeConfig(tenant.plano).label}</span>
                </button>
              )}

              <button 
                onClick={() => toggleDarkMode()}
                title={isDarkMode ? "Ativar Modo Claro" : "Ativar Modo Escuro"}
                className="p-1.5 md:p-2 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white rounded-full transition-colors active:scale-95"
              >
                {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>

              {/* <NotificationBellDropdown /> — REMOVIDO v29.1 */}

              <button
                 onClick={handleLogout}
                 className="hidden md:flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                 title="Sair"
              >
                <LogOut size={16} />
                <span>Sair</span>
              </button>
            </div>
          </header>

        {/* Content Area */}
        <div ref={mainContentRef} className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>

      <UpgradeModal 
        isOpen={isUpgradeModalOpen} 
        onClose={() => setIsUpgradeModalOpen(false)} 
        initialMessage={upgradeMessage} 
      />
    </div>
  );
}
